import NurturingHistory, { CreateShortUrlRequest } from 'app/apiService/nurturingHistory/nurturingHistoryApiService';
import { notificationAlertActions } from 'app/pages/NotificationAlert/slice';

export interface NurturingHistoryParams {
  emailAddress?: string;
  opportunityId: string;
  dealerId: string;
  rowDetails?: any;
  analytics?: boolean;
}

export const handleNurturingHistoryRedirect = async (
  params: NurturingHistoryParams,
  dispatch: any,
  history: any,
  currentBreadcrumbs?: any[],
): Promise<void> => {
  try {
    // Extract email from rowDetails if not provided directly
    const emailAddress = params.emailAddress ||
      params.rowDetails?.email_address ||
      params.rowDetails?.emailAddress ||
      params.rowDetails?.email ||
      params.rowDetails?.Email_mailshake ||
      '';

    if (!emailAddress) {
      throw new Error('Email address is required for nurturing history');
    }

    if (!params.opportunityId) {
      throw new Error('Opportunity ID is required for nurturing history');
    }

    if (!params.dealerId) {
      throw new Error('Dealer ID is required for nurturing history');
    }

    // Prepare payload for API call
    const payload: CreateShortUrlRequest = {
      emailAddress,
      opportunityId: params.opportunityId,
      dealerId: params.dealerId,
      analytics: params.analytics,
    };

    console.log('Creating short URL with payload:', payload);

    // Call API to create short URL
    const response = await NurturingHistory.createShortUrl(payload);

    if (response.statusCode !== 201 && response.statusCode !== 200) {
      throw new Error(response.message || 'Failed to create short URL');
    }

    const shortUrl = response.body?.shortUrl;

    if (!shortUrl) {
      throw new Error('Short URL not received from API response');
    }

    console.log('Short URL created:', shortUrl);

    // Extract URL ID from the short URL
    const urlId = NurturingHistory.extractUrlId(shortUrl);

    if (!urlId) {
      throw new Error('Could not extract URL ID from short URL');
    }

    console.log('Extracted URL ID:', urlId);

    // Prepare breadcrumbs for the nurturing history page
    const nurturingHistoryBreadcrumbs = [
      ...(currentBreadcrumbs || []).map(breadcrumb => ({ ...breadcrumb, isActive: false })),
      {
        label: `Nurturing History (${emailAddress})`,
        link: `/url/${urlId}`,
        isActive: true
      }
    ];

    // Navigate to the URL with proper breadcrumbs immediately
    // Use window.location for external navigation from AI Avatar Layout
    const targetUrl = `/url/${urlId}`;
    const currentPath = window.location.pathname;
    
    // Check if we're in AI Avatar Layout
    const isInAIAvatarLayout = currentPath.includes('/ai-avatar-layout');
    
    // State data to pass to nurturing history page
    const navigationState = {
      breadcrumbsList: nurturingHistoryBreadcrumbs,
      fromPage: 'MeasurePage',
      fromAIAvatarLayout: isInAIAvatarLayout,
      emailAddress,
      opportunityId: params.opportunityId,
      dealerId: params.dealerId
    };
    
    if (isInAIAvatarLayout) {
      // Store breadcrumbs state in sessionStorage before navigation
      // This is needed because window.location.href doesn't preserve React Router state
      try {
        sessionStorage.setItem('nurturing_history_breadcrumbs', JSON.stringify(navigationState));
        console.log('Stored breadcrumbs in sessionStorage for AI Avatar Layout navigation:', navigationState);
      } catch (e) {
        console.error('Failed to store breadcrumbs in sessionStorage:', e);
      }
      
      // Use window.location to break out of nested routing
      window.location.href = targetUrl;
    } else {
      // Use history.push for normal navigation
      history.push(targetUrl, navigationState);
    }

  } catch (error) {
    console.error('Error creating nurturing history link:', error);

    // Show error notification
    dispatch(
      notificationAlertActions.addNotification({
        open: true,
        severity: 'error',
        message: error instanceof Error ? error.message : 'Failed to create nurturing history link',
      }),
    );
  }
};

export default {
  handleNurturingHistoryRedirect,
}; 